import { useCallback, useEffect, useRef } from "react";

type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
};

type ControlFunctions = {
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
};

export type DebouncedState<T extends (...args: never[]) => unknown> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) &
  ControlFunctions;

export function useDebounceCallback<T extends (...args: never[]) => unknown>(
  func: T,
  delay = 500,
  _options: DebounceOptions = {},
): DebouncedState<T> {
  const funcRef = useRef(func);
  funcRef.current = func;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingRef.current = false;
  }, []);

  const flush = useCallback(() => {
    cancel();
  }, [cancel]);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      pendingRef.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        pendingRef.current = false;
        timeoutRef.current = null;
        funcRef.current(...args);
      }, delay);
    },
    [delay],
  ) as DebouncedState<T>;

  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.isPending = () => pendingRef.current;

  useEffect(() => cancel, [cancel]);

  return debounced;
}
